import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { FetchCallbacksContext } from '../../FetchCallbacks';
import fetchAPI from '../../../utils/fetchAPI';
import FormBuilderThirdfortView from '../view/FormBuilderThirdfortView';
import getApiEndpoints, {
  EndpointTypes
} from '../../../components/form/FormEndpoints';
import useTranslations from '../../../hooks/useTranslations';
import { LOAD_FORM } from '../../../store/modules/state/formBuilder';

const TAB_VALUES = {
  AUTHENTICATION: 0,
  SETTINGS: 1
};

const AUTHENTICATION_PAGES = {
  BASIC: 0,
  FILE: 1,
  DETAILS: 2
};

const FormBuilderThirdfortEngine = ({
  formId,
  integrationData: receivedIntegrationData,
  dispatch
}) => {
  const t = useTranslations();
  const { openSnackbar } = useContext(FetchCallbacksContext);
  const apiEndpoints = useMemo(() => getApiEndpoints(EndpointTypes.ADMIN), []);
  const [activeTab, setActiveTab] = useState(TAB_VALUES.AUTHENTICATION);
  const [authenticationPage, setAuthenticationPage] = useState(
    AUTHENTICATION_PAGES.BASIC
  );
  const [thirdfortConnected, setThirdfortConnected] = useState(
    receivedIntegrationData && receivedIntegrationData.id !== 'new'
  );
  const [privateKey, setPrivateKey] = useState('');
  const [kid, setKid] = useState('');
  const [username, setUsername] = useState('');
  const [isDisconnectDialogOpen, setIsDisconnectDialogOpen] = useState(false);
  const [existingFiles, setExistingFiles] = useState([]);
  const [isOngoing, setIsOngoing] = useState(true);
  const [showingUser, setShowingUser] = useState({});

  const [integrationData, setIntegrationData] = useState(
    receivedIntegrationData
  );

  const switchChangedManually = useRef(false);

  const fillStateValuesWithCurrentInfo = (valuesObject = {}) => {
    setIsOngoing(valuesObject.isOngoing);
    setPrivateKey(valuesObject.privateKey);
    setKid(valuesObject.kid);
    setUsername(valuesObject.username);
    setIntegrationData(valuesObject.integrationData);
    setExistingFiles(valuesObject.existingFiles);
    setShowingUser(valuesObject.showingUser);
  };

  const getCurrentInfo = () => {
    fetchAPI(
      `/scheduler/v1/integration/${integrationData.id}/serverdetails`
    ).then(res => {
      if (!res || !res.integrationServerDetails) {
        return;
      }
      const stateValues = {
        isOngoing:
          res?.integration?.additionalParams?.ongoingCheckOptions?.ongoing ||
          false,
        kid: res?.integrationServerDetails?.kid || '',
        username: res?.integrationServerDetails?.username || '',
        integrationData: res,
        existingFiles: res?.integrationServerDetails?.privateKey
          ? [
              {
                name: t('forms.field.fileupload.upload.existing'),
                size: 887,
                complete: true,
                progress: 100
              }
            ]
          : []
      };
      if (res?.integrationServerDetails?.systemUserId) {
        fetchAPI(
          `/users/v1/users/${res?.integrationServerDetails?.systemUserId}`
        ).then(userRes => {
          if (userRes?.info) {
            stateValues.showingUser = {
              ...userRes.info,
              label:
                '' + userRes.info?.firstName + ' ' + userRes.info?.lastName,
              value: userRes.id
            };
          }
          fillStateValuesWithCurrentInfo(stateValues);
        });
      } else {
        stateValues.showingUser = {};
        fillStateValuesWithCurrentInfo(stateValues);
      }
    });
  };

  useEffect(() => {
    if (integrationData && integrationData.id && integrationData.id !== 'new') {
      getCurrentInfo();
    }
  }, [integrationData]);

  const resetFormToDefaults = () => {
    setThirdfortConnected(false);
    setPrivateKey('');
    setKid('');
    setUsername('');
    setExistingFiles([]);
    setShowingUser({});
  };

  const saveChanges = () => {
    let newIntegrationData = { ...integrationData };
    if (!newIntegrationData.integration) {
      newIntegrationData.integration = {};
    }
    if (newIntegrationData.id === 'new') {
      delete newIntegrationData.id;
    }
    newIntegrationData.integration.assetId =
      newIntegrationData.assetId || formId;
    newIntegrationData.integration.integrationType =
      newIntegrationData.integrationType || 'thirdfort';
    if (!newIntegrationData.integration.additionalParams) {
      newIntegrationData.integration.additionalParams = {};
    }
    if (!newIntegrationData.integration.additionalParams.ongoingCheckOptions) {
      newIntegrationData.integration.additionalParams.ongoingCheckOptions = {};
    }
    newIntegrationData.integration.additionalParams.ongoingCheckOptions.ongoing =
      isOngoing;
    // rename integrationServerDetails to ftp_details
    newIntegrationData.ftp_details =
      newIntegrationData.integrationServerDetails || {};
    delete newIntegrationData.integrationServerDetails;

    //update the three main fields if necessary
    if (kid) {
      newIntegrationData.ftp_details.kid = kid;
    }
    if (username) {
      newIntegrationData.ftp_details.username = username;
    }

    //update the user_info details
    if (!newIntegrationData.user_info) {
      newIntegrationData.user_info = {};
    }
    if (!newIntegrationData.user_info.info) {
      newIntegrationData.user_info.info = {};
    }

    if (showingUser && showingUser.value) {
      newIntegrationData.user_info.id = showingUser.value;
      newIntegrationData.user_info.info.email = showingUser.email;
      newIntegrationData.user_info.info.userName = showingUser.email;
      newIntegrationData.user_info.info.firstName = showingUser.firstName;
      newIntegrationData.user_info.info.lastName = showingUser.lastName;
    }

    const _showingUser = showingUser;

    //"active\",\"additionalParams\",\"assetId\",\"id\",\"integrationType\",\"integration_server_code\",\"responseInfo\",\"triggers
    /*delete newIntegrationData.active;
    delete newIntegrationData.additionalParams;
    delete newIntegrationData.assetId;
    delete newIntegrationData.id;
    delete newIntegrationData.integrationType;
    delete newIntegrationData.integration_server_code;
    delete newIntegrationData.responseInfo;
    delete newIntegrationData.triggers;*/

    if (privateKey) {
      newIntegrationData.ftp_details.privateKey = privateKey;
    }

    fetchAPI('/scheduler/v1/integration/connect/thirdfort', {
      method: 'PUT',
      body: newIntegrationData,
      handleResponse: r => {
        const newIntegrationId = r.headers.map.location;
        setIntegrationData({ id: newIntegrationId });
        setThirdfortConnected(true);
        openSnackbar(
          'success',
          `snackbars.integrations.thirdfort_save_success`
        );
      }
    }).catch(() => {
      openSnackbar('error', 'snackbars.integrations.thirdfort_save_failure');
    });
  };

  const onSaveBtnClicked = useCallback(() => {
    saveChanges();
  }, [kid, formId, username, isOngoing, showingUser]);

  const onDisconnectBtnClicked = useCallback(() => {
    setIsDisconnectDialogOpen(false);
    if (
      !integrationData ||
      !integrationData.id ||
      integrationData.id === 'new'
    ) {
      return;
    }
    fetchAPI(
      `/forms/v1/forms/${formId}/integration/${integrationData.id}/deactivate?integrationType=thirdfort`,
      {
        method: 'DELETE'
      }
    )
      .then(res => {
        fetchAPI(`/forms/v1/forms/${formId}`).then(r => {
          dispatch({ type: LOAD_FORM, payload: { ...r, id: formId } });
          resetFormToDefaults();
          openSnackbar(
            'success',
            'snackbars.integrations.thirdfort_disconnect_success'
          );
        });
      })
      .catch(() => {
        openSnackbar(
          'error',
          'snackbars.integrations.thirdfort_disconnect_failure'
        );
      });
  }, [apiEndpoints, integrationData, kid]);

  const onFileDropped = useCallback(file => {
    const formData = new FormData();

    formData.append('validate', true);
    formData.append('file', file);

    fetchAPI(`/scheduler/v1/tf/extract/pk`, {
      method: 'POST',
      body: formData
    })
      .then(results => {
        if (results?.privateKey) {
          setPrivateKey(results.privateKey);
          setAuthenticationPage(AUTHENTICATION_PAGES.DETAILS);
          openSnackbar(
            'success',
            'snackbars.integrations.thirdfort_upload_success'
          );
        } else {
          setPrivateKey('');
          openSnackbar(
            'error',
            'snackbars.integrations.thirdfort_upload_fail',
            true
          );
        }
      })
      .catch(e => {
        setPrivateKey('');
        openSnackbar(
          'error',
          'snackbars.integrations.thirdfort_upload_fail',
          true
        );
      });
  }, []);

  const onToggleSwitch = useCallback(
    e => {
      if (/*!secret || !groupId ||*/ !thirdfortConnected) {
        openSnackbar(
          'error',
          `snackbars.integrations.${integrationType}_out_of_order`
        );
        return;
      }

      const checked = e.target.checked;
      const newValue = checked ? true : false;
      switchChangedManually.current = true;
      setIsOngoing(newValue);
    },
    [thirdfortConnected, integrationData]
  );

  const onOwnCredentialsClicked = () => {
    setAuthenticationPage(AUTHENTICATION_PAGES.FILE);
  };

  const openMenuOptionParser = opt => {
    const userTypeLabel =
      opt.value.data.schema === 'external' ? 'External user' : 'Internal user';
    return (
      <p style={{ margin: 0 }}>
        {opt.value.data.firstName + ' ' + opt.value.data.lastName}
        <br />
        <small>
          {opt.value.data.email}
          {opt.value.data.schema && form.allowExternalUserAccounts ? (
            <>
              &nbsp;&nbsp;&nbsp;◀●▶&nbsp;&nbsp;&nbsp;
              {userTypeLabel}
            </>
          ) : (
            ''
          )}
        </small>
      </p>
    );
  };

  const changedDropdown = val => {
    let flattenedVal;
    if (!val) {
      return;
    }
    flattenedVal = {
      ...val.data,
      label: '' + val.data.firstName + ' ' + val.data.lastName,
      value: val.value
    };
    setShowingUser(flattenedVal);
  };

  const getUserLabel = u => {
    return {
      text: u.info.firstName + ' ' + u.info.lastName,
      value: {
        label: u.info.firstName + ' ' + u.info.lastName,
        value: u.id,
        data: {
          firstName: u.info.firstName,
          lastName: u.info.lastName,
          email: u.info.email
        }
      }
    };
  };

  const loadOptions = v => {
    return fetchAPI(
      `/users/v1/users?offset=0&role=admin&limit=100&sort='info'->'firstName' asc&query=[
        [{"field":"'status'","op":"=","value":"'ACTIVE'"}],
        [
          {"field": "'info'->'email'", "op": "ILIKE", "value": "%${v}%", "internOp": "OR"},
          {"field": "'info'->'lastName'", "op": "ILIKE", "value": "%${v}%", "internOp": "OR"},
          {"field": "'info'->'userName'", "op": "ILIKE", "value": "%${v}%", "internOp": "OR"},
          {"field": "'info'->'firstName'", "op": "ILIKE", "value": "%${v}%", "internOp": "OR"}
      ],
      [{"field":"'schema'", "op": "=", "value": "'internal'"}]
    ]`.replace(/\n\s+/g, '    ')
    ).then(({ results }) => {
      return results.map(getUserLabel);
    });
  };

  useEffect(() => {
    if (!switchChangedManually.current) {
      return;
    }
    switchChangedManually.current = false;
    saveChanges();
  }, [switchChangedManually, isOngoing]);

  const viewProps = {
    activeTab,
    setActiveTab,
    TAB_VALUES,
    existingFiles,
    kid,
    setKid,
    username,
    setUsername,
    thirdfortConnected,
    onFileDropped,
    onSaveBtnClicked,
    onDisconnectBtnClicked,
    isDisconnectDialogOpen,
    onToggleSwitch,
    setIsDisconnectDialogOpen,
    isOngoing,
    showingUser,
    openMenuOptionParser,
    changedDropdown,
    loadOptions,
    onOwnCredentialsClicked,
    authenticationPage
  };

  return <FormBuilderThirdfortView {...viewProps} />;
};

export default FormBuilderThirdfortEngine;
